<template>
  <v-container grid-list-xl fluid>
    <v-layout row wrap>
      <v-progress-linear
        indeterminate
        :active="isRequestingNow.length > 0"
        color="blue"
        class="mb-0"
      ></v-progress-linear>
      <v-overlay :value="pageLoading">
        <v-progress-circular indeterminate size="64"></v-progress-circular>
      </v-overlay>
      <v-flex v-if="!pageLoading && item" style="position: relative">
        <v-container class="grey lighten-5">
          <v-row no-gutters class="pagination-table-header">
            <copy-button
              style="position:absolute; top: 0; right: 0;"
              @click="copyToClipboard(item)"
            />
            <v-col
              cols="12"
              sm="4"
              class="py-md-0"
            >
              <datetime-picker
                v-model="item.datetime"
                prepend-icon="mdi-calendar-clock"
                :label="lang($store, 'sap/listings.global.filter.hints.datetime')"
                :rules="[rules.required]"
                :locale="getCurrentLocale()"
                :loading="isRequestingNow.includes('datetime')"
              />
            </v-col>
            <v-col
              cols="12"
              sm="4"
              class="py-md-0"
            >
              <v-select
                v-model="item.order_complex_type"
                :items="orderComplexTypeItems"
                prepend-icon="mdi-format-list-bulleted-type"
                single-line
                :hint="lang($store, 'sap/listings.global.filter.hints.order_base_type')"
                persistent-hint
                :error-messages="(errors && errors.order_complex_type)?errors.order_complex_type:null"
                @change="removeError('order_complex_type')"
                :loading="isRequestingNow.includes('order_complex_type')"
              ></v-select>
            </v-col>
            <v-col
              cols="12"
              sm="4"
              class="py-md-0"
            >
              <v-select
                v-model="item.order_status"
                :items="orderStatusItems"
                prepend-icon="mdi-cart-arrow-right"
                single-line
                :hint="lang($store, 'sap/listings.global.filter.hints.order_status')"
                persistent-hint
                :error-messages="(errors && errors.order_status)?errors.order_status:null"
                @change="removeError('order_status')"
                :loading="isRequestingNow.includes('order_status')"
              ></v-select>
            </v-col>
            <v-col
              cols="12"
              sm="4"
              class="py-md-0"
            >
              <v-select
                v-model="item.tracker"
                :items="trackerItems"
                single-line
                :hint="lang($store, 'sap/listings.global.filter.hints.tracker')"
                persistent-hint
                :error-messages="(errors && errors.tracker)?errors.tracker:null"
                @change="removeError('tracker'); updateRequest('tracker', $event)"
                :loading="isRequestingNow.includes('tracker')"
              ></v-select>
            </v-col>
            <v-col
              cols="8"
              class="py-md-0 d-flex flex-row justify-space-around align-center"
            >

              <is-ok :is-ok="item.is_contact_ok"
                     @updated="item.is_contact_ok = $event;"
                     :loading="isRequestingNow.includes('is_contact_ok')"
                     :small="false"
              />
              <is-ok icon="mdi-spray-bottle"
                     :is-ok="item.is_cleaner_ok"
                     @updated="item.is_cleaner_ok = $event;"
                     :loading="isRequestingNow.includes('is_cleaner_ok')"
                     :small="false"
              />
              <is-payed
                :is-ok="item.is_payed"
                @updated="item.is_payed = $event;"
                :loading="isRequestingNow.includes('is_payed')"
                style="font-size: 24px"
              />
              <is-presence
                :is-ok="item.is_presence"
                @updated="item.is_presence = $event;"
                :loading="isRequestingNow.includes('is_presence')"
                :small="false"
              />
            </v-col>
            <v-col
              cols="12"
              md="6"
              class="py-md-1"
            >
              <address-selector
                :required="true"
                :disable-close="true"
                :selected-address="item.address"
              />
              <div>
                <v-text-field
                  v-model="item.minicomment"
                  :counter="200"
                  :rules="[rules.max(200)]"
                  prepend-icon="mdi-tooltip-text-outline"
                  :label="lang($store, 'sap/listings.global.filter.hints.minicomment')"
                  autocomplete="off"
                  :error-messages="(errors && errors.minicomment)?errors.minicomment:null"
                  @input="removeError('minicomment')"
                  class="pt-2"
                  @change="updateRequest('minicomment', $event)"
                  :loading="isRequestingNow.includes('minicomment')"
                ></v-text-field>
              </div>
              <div>
                <v-text-field
                  v-model="item.minicomment2"
                  @change="updateRequest('minicomment2', $event)"
                  :counter="200"
                  :rules="[rules.max(200)]"
                  prepend-icon="mdi-tooltip-text-outline"
                  :label="lang($store, 'sap/listings.global.filter.hints.minicomment2')"
                  autocomplete="off"
                  :error-messages="(errors && errors.minicomment2)?errors.minicomment2:null"
                  @input="removeError('minicomment2')"
                  class="pt-0"
                  :loading="isRequestingNow.includes('minicomment2')"
                ></v-text-field>
              </div>
            </v-col>
            <v-col
              cols="12"
              md="6"
              class="py-md-1 pl-md-3"
            >
              <cleaner-selector
                :selected-cleaner="item.foreman"
                @select="item.foreman = $event; updateRequest('foreman_uuid', ($event && $event.uuid)?$event.uuid:null)"
                :role="FOREMAN"
                :label="lang($store, 'sap/listings.global.filter.hints.search_foreman')"
                :bg-color="'#efe8f0'"
                :dateTime="dateIso"
              />
              <cleaners-selector
                :selected-cleaners="item.cleaners"
                @updated="item.cleaners = $event; updateCleaners()"
                :error-messages="(errors && errors.cleaners_uuid)?errors.cleaners_uuid:null"
                :role="CLEANER_COMPLEX"
                :dateTime="dateIso"
              />

              <div class="mb-2">
                <div>{{ lang($store, 'sap/listings.global.filter.hints.revenue') }}</div>
                <div class="pl-2 v-list v-sheet theme--light v-list--dense v-list--subheader v-list--two-line">
                  <v-text-field
                    type="number"
                    v-model="item.revenue"
                    prepend-icon="mdi-cash-100"
                    :label="$t('table.headers.sum')"
                    autocomplete="off"
                    :error-messages="(errors && errors.revenue)?errors.revenue:null"
                    @input="removeError('revenue')"
                    @change="updateRequest('revenue', $event)"
                    :loading="isRequestingNow.includes('revenue')"
                  ></v-text-field>
                </div>
              </div>
            </v-col>
          </v-row>
          <v-col
            v-if="!!(item.review)"
            cols="12"
            md="6"
            class="py-md-1"
          >
            <view-rating :review="item.review"/>
          </v-col>
          <v-row>
            <v-col
              cols="12"
              md="6"
              class="py-md-0"
            >
              <div>
                <v-select
                  v-model="item.source"
                  :items="sourcesItems"
                  :background-color="SOURCES_COLORS[item.source]
                ?SOURCES_COLORS[item.source]
                :'white'"
                  :item-color="SOURCES_COLORS[item.source] && item.source !== SOURCES.UNSET
                ?SOURCES_COLORS[item.source]
                 :'black'"
                  class="invert-select"
                  :class="item.source"
                  prepend-icon="mdi-arrow-decision-outline"
                  single-line
                  :hint="lang($store, 'sap/listings.global.filter.hints.source')"
                  persistent-hint
                  :error-messages="(errors && errors.source)?errors.source:null"
                  @change="updateRequest('source', $event); removeError('source')"
                  :loading="isRequestingNow.includes('source')"
                ></v-select>
              </div>
              <div class="d-flex mt-4">
                <v-checkbox
                  v-model="item.is_manual_price"
                  color="yellow darken-2"
                  @click.prevent
                  :false-value="0"
                  :true-value="1"
                ></v-checkbox>
                <v-text-field
                  v-model="item.manual_price"
                  :rules="[rules.int_or_null, rules.max(6)]"
                  :label="lang($store, 'sap/listings.global.filter.hints.manual_price')"
                  autocomplete="off"
                  :disabled="!item.is_manual_price"
                  :error-messages="(errors && 'custom_price_data.manual_price' in errors)?lang($store, 'sap/listings.global.validation.price_common'):null"
                  @input="removeError('manual_price')"
                ></v-text-field>
              </div>
              <div>
                <v-text-field
                  v-model="item.note"
                  :counter="200"
                  :rules="[rules.max(200)]"
                  prepend-icon="mdi-tooltip-text-outline"
                  :label="lang($store, 'sap/listings.global.filter.hints.note')"
                  autocomplete="off"
                  :error-messages="(errors && errors.note)?errors.note:null"
                  @input="removeError('note')"
                  @change="updateRequest('note', $event)"
                  :loading="isRequestingNow.includes('note')"
                ></v-text-field>
              </div>
              <div>
                <v-textarea
                  v-model="item.additional_info"
                  :counter="3000"
                  :rules="[rules.max(3000)]"
                  prepend-icon="mdi-account-badge-horizontal-outline"
                  :label="lang($store, 'sap/listings.global.filter.hints.additional_info')"
                  autocomplete="off"
                  :error-messages="(errors && errors.additional_info)?errors.additional_info:null"
                  @input="removeError('additional_info')"
                  @change="updateRequest('additional_info', $event)"
                  :loading="isRequestingNow.includes('additional_info')"
                ></v-textarea>
              </div>
              <div>
                <v-text-field
                  v-model="item.promocode"
                  :counter="50"
                  :rules="[rules.max(50)]"
                  prepend-icon="mdi-tooltip-text-outline"
                  :label="lang($store, 'sap/listings.global.filter.hints.promocode')"
                  autocomplete="off"
                  :error-messages="(errors && errors.promocode)?errors.promocode:null"
                  @input="removeError('promocode')"
                  @change="updateRequest('promocode', $event)"
                  :loading="isRequestingNow.includes('promocode')"
                ></v-text-field>
              </div>
              <div>
                <v-textarea
                  v-model="item.contact_feedback"
                  :counter="3000"
                  :rules="[rules.max(3000)]"
                  prepend-icon="mdi-account-badge-horizontal-outline"
                  :label="lang($store, 'sap/listings.global.filter.hints.contact_feedback')"
                  autocomplete="off"
                  :error-messages="(errors && errors.contact_feedback)?errors.contact_feedback:null"
                  @input="removeError('contact_feedback')"
                  @change="updateRequest('contact_feedback', $event)"
                  :loading="isRequestingNow.includes('contact_feedback')"
                ></v-textarea>
              </div>
              <div>
                <vue-dropzone
                  ref="dropzone"
                  id="orderAttachmentsUploader"
                  :options="dropOptionsDefault"
                  @vdropzone-complete="afterComplete"
                  @vdropzone-success="onFileUploaded"
                  @vdropzone-error="onFileError"
                ></vue-dropzone>
              </div>
              <div v-if="item.attachments.length > 0" style="margin-bottom: 85px;">
                <v-row>
                  <v-col
                    cols="12"
                    md="3"
                    sm="4"
                    class="py-md-0"
                    v-for="attachment in item.attachments"
                    :key="attachment.id"
                  >
                    <div class="preview-attachment">
                      <a :href="attachment.link" target="_blank" class="preview-attachment__content">
                        <img v-if="isImage(attachment.link)" :src="attachment.link" :alt="attachment.link">
                        <div class="text" v-else>.{{attachment.name.split('.').pop()}}</div>
                      </a>
                      <v-btn
                        x-small fab dark falt fixed top absolute
                        right="right"
                        class="setting-fab"
                        color="red"
                        style="position: absolute; right: 0%; top: 0%;"
                        @click="deleteFile(attachment.id)"
                      >
                        <v-icon>mdi-close</v-icon>
                      </v-btn>
                    </div>
                  </v-col>
                </v-row>
              </div>
            </v-col>
            <v-col
              cols="12"
              md="6"
              class="py-md-0"
            >
              <div
              >
                <div>{{ $t('payments.title') }}</div>
                <payments-list
                  :payable-type="ORDERS_COMPLEX"
                  :payable="item"
                />
              </div>
            </v-col>
          </v-row>
          <v-btn
            small fab dark falt fixed
            right="right"
            class="setting-fab"
            color="red"
            style="top: 54%"
            @click="isShowHistory = true"
            title="Посмотреть историю изменений"
          >
            <v-icon>mdi-history</v-icon>
          </v-btn>
          <v-navigation-drawer
            class="setting-drawer"
            style="z-index: 99;"
            right v-model="isShowHistory" fixed>
            <keep-alive>
              <activity-list
                :args="[this.item.uuid]"
                method="orderComplexActivity"
              />
            </keep-alive>
            <div
              class="pa-1"
              @click="isShowHistory = false"
              style="opacity: 0.8; cursor: pointer; background-color: gray;text-align: right; position: absolute; top: 10px; right: 25px; border-radius: 50%; overflow: hidden">
              <v-icon>mdi-close</v-icon>
            </div>
          </v-navigation-drawer>
          <negative-feedback-comment
            title="Создать/посмотреть негатив"
            @click="handleNegativeFeedbackClick"
            />
          <damage-button
            title="Создать/посмотреть ущерб"
            @click="damageClick"
          />
          <accept-dialog
            :is-open-dialog="isShowCreateNegativeDialog"
            @on-close="isShowCreateNegativeDialog = false"
            @on-accept="negativeFeedbackClick"
          >
            <div class="pt-6">
              {{ lang($store, 'sap/listings.global.dialogs.negatives.question') }}
            </div>
          </accept-dialog>
        </v-container>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import moment from 'moment';
import debounce from 'lodash/debounce';
import VueDropzone from 'vue2-dropzone';
/* constants */
import { SOURCES_COLORS } from '@/constants/sources_data';
import { ORDERS_COMPLEX } from '@/constants/payable';
import { DATETIME_ISO } from '@/constants/dates';
import { FOREMAN, CLEANER_COMPLEX } from '@/constants/roles';
import * as SOURCES from '~constants/sources';
/* utils */
import { prepareValidationMessages } from '@/utils';
/* proxies */
import OrderComplexProxy from '@/proxies/Order/OrderComplexProxy';
/* mixins */
import updatePageContentMixin from '@/mixins/updatePageContentMixin';
import orderCopyToClipBoardMixin from '@/mixins/orderCopyToClipBoardMixin';
import validationMixin from '@/mixins/validationMixin';
import needAdditionalServicesFilter from '@/mixins/filters/needAdditionalServicesFilter';
import orderComplexTypeFilter from '@/mixins/filters/orderComplexTypeFilter';
import orderStatusFilter from '@/mixins/filters/orderStatusFilter';
import trackerFilter from '@/mixins/filters/trackerFilter';
import orderBaseUpdateSimpleProp from '@/mixins/orders/orderUpdateSimpleProp';
import sourcesFilter from '@/mixins/filters/sourcesFilter';
import negatable from '@/mixins/negatives/negatable';
import damagable from '@/mixins/damages/damagable';
/* components */
import CopyButton from '@/components/minialerts/CopyButton.vue';
import DatetimePicker from '@/components/DatetimePicker.vue';
import IsOk from '@/components/minialerts/IsOk.vue';
import IsPayed from '@/components/minialerts/IsPayed.vue';
import IsPresence from '@/components/minialerts/IsPresence.vue';
import AddressSelector from '@/components/autocomples/AddressSelector.vue';
import CleanerSelector from '@/components/autocomples/CleanerSelector.vue';
import CleanersSelector from '@/components/autocomples/CleanersSelector.vue';
import PaymentsList from '@/components/listings/PaymentsList.vue';
import ActivityList from '@/components/activity/ActivityList.vue';
import ViewRating from '@/components/listings/Orders/ViewRating.vue';
import AcceptDialog from '@/components/AcceptDialog.vue';
import i18n from '@/i18n';

export default {
  name: 'ShowComplex',
  mixins: [
    updatePageContentMixin,
    orderCopyToClipBoardMixin,
    validationMixin,
    needAdditionalServicesFilter,
    orderComplexTypeFilter,
    orderStatusFilter,
    orderBaseUpdateSimpleProp,
    sourcesFilter,
    trackerFilter,
    negatable,
    damagable,
  ],
  components: {
    CopyButton,
    DatetimePicker,
    IsOk,
    IsPayed,
    IsPresence,
    AddressSelector,
    CleanerSelector,
    CleanersSelector,
    PaymentsList,
    ActivityList,
    ViewRating,
    AcceptDialog,
    VueDropzone,
  },
  data() {
    return {
      SOURCES,
      SOURCES_COLORS,
      ORDERS_COMPLEX,
      FOREMAN,
      CLEANER_COMPLEX,
      reportMethodName: 'ordersBaseListingReport',
      negatableType: 'complex',
      damagableType: 'complex',
      pageLoading: false,
      isShowHistory: false,
      shouldSeeLoaderOnUpdating: true,
      isRequestingNow: [],
      item: null,
      useNullableOrderComplexType: false,
      useNullableOrderStatus: false,
      useNullableTracker: false,
      useNullableSource: false,
      isShowCreateNegativeDialog: false,
      errors: {},
      dropOptions: {
        method: 'post',
        maxFilesize: 30, // MB
        maxFiles: 20,
        clickable: true,
        // acceptedFiles: '.png,.jpg,.jpeg,.mov',
        // chunking: true,
        // chunkSize: 500, // Bytes
        // thumbnailWidth: 150, // px
        // thumbnailHeight: 150,
        addRemoveLinks: true,
        headers: {
          'Cache-Control': null,
          'X-Requested-With': null,
          Authorization: `Bearer ${localStorage.getItem('id_token')}`,
          Accept: 'application/json',
        },
        dictDefaultMessage: i18n.t('uploads.dictDefaultMessage'),
        dictFallbackMessage: i18n.t('uploads.dictFallbackMessage'),
        dictFallbackText: i18n.t('uploads.dictFallbackText'),
        dictFileTooBig: i18n.t('uploads.dictFileTooBig'),
        dictInvalidFileType: i18n.t('uploads.dictInvalidFileType'),
        dictResponseError: i18n.t('uploads.dictResponseError'),
        dictCancelUpload: i18n.t('uploads.dictCancelUpload'),
        dictCancelUploadConfirmation: i18n.t('uploads.dictCancelUploadConfirmation'),
        dictRemoveFile: i18n.t('uploads.dictRemoveFile'),
        dictMaxFilesExceeded: i18n.t('uploads.dictMaxFilesExceeded'),
      },
    };
  },
  created() {
    this.pageLoading = true;
    this.fetchData();
  },
  watch: {
    '$route.params.orderUuid': {
      handler() {
        this.fetchData();
      },
    },
    'item.datetime': {
      handler(newVal) {
        this.updateRequest('datetime', moment(newVal).format(DATETIME_ISO));
      },
    },
    'item.order_complex_type': {
      handler(newVal) {
        this.updateRequest('order_complex_type', newVal);
      },
    },
    'item.order_status': {
      handler(newVal) {
        this.updateRequest('order_status', newVal);
      },
    },
    'item.is_cleaner_ok': {
      handler(newVal) {
        this.updateRequest('is_cleaner_ok', newVal);
      },
    },
    'item.is_contact_ok': {
      handler(newVal) {
        this.updateRequest('is_contact_ok', newVal);
      },
    },
    'item.is_payed': {
      handler(newVal) {
        this.updateRequest('is_payed', newVal);
      },
    },
    'item.is_presence': {
      handler(newVal) {
        this.updateRequest('is_presence', newVal);
      },
    },
    'item.is_manual_price': {
      handler(newVal) {
        if (!this.pageLoading){
          this.item.manual_price = newVal ? 0 : null;
        }
      }
    },
    'item.manual_price': debounce(function (newVal) {
      this.updateRequest('custom_price_data', {
        is_manual_price: this.item.is_manual_price,
        manual_price: this.item.manual_price
      });
    }, 1000),
    'item.need_additional_services': {
      handler(newVal) {
        this.updateRequest('need_additional_services', newVal);
      },
    },
  },
  computed: {
    dateIso() {
      return this.item.datetime ? moment(this.item.datetime).toISOString() : null;
    },
    dropOptionsDefault() {
      const ob = Object.assign({}, this.dropOptions);
      ob.url = `${process.env.VUE_APP_API_LOCATION}/orders-complex/` + this.$route.params.orderUuid + `/attachments`;
      return ob;
    },
  },
  methods: {
    fetchData() {
      new OrderComplexProxy().find(this.$route.params.orderUuid)
        .then(response => {
          const { order } = response.data.data;
          this.processFetchedData(order);
          this.$route.meta.title = `Заказ: ${this.item.datetime}`;
        })
        .catch(error => {
          console.error(error);
          return this.$router.push({
            name: 404,
          });
        })
        .finally(() => {
          this.pageLoading = false;
          this.$store.dispatch('page/finishUpdateCurrentPageContent');
        });
    },
    processFetchedData(order) {
      const responseOrder = Object.assign({}, order);
      this.$set(responseOrder, 'is_manual_price', order.manual_price === null ? 0 : 1);
      this.item = responseOrder;
      this.$nextTick(() => {
        this.item.manual_price = order.manual_price;
      });
    },
    getUpdateSimpleRequestClient() {
      return new OrderComplexProxy();
    },
    updateRequest(property, val) {
      if (!this.pageLoading) {
        this.errors = {};
        this.isRequestingNow.push(property);
        (this.updateSimpleRequest(this.item, property))(val)
          .catch(response => {
            this.errors = prepareValidationMessages(response.data.data);
          })
          .finally(() => {
            const index = this.isRequestingNow.indexOf(property);
            if (index !== -1) this.isRequestingNow.splice(index, 1);
          });
      }
    },
    handleNegativeFeedbackClick() {
      if (!this.item.negative) {
        this.isShowCreateNegativeDialog=true;
      } else {
        this.negativeFeedbackClick();
      }
    },
    afterComplete(file) {
      this.$refs.dropzone.removeFile(file);
    },
    onFileError(file, response) {
      const errors = prepareValidationMessages(response.data);
      if (errors.file) {
        this.$store.dispatch('page/displaySnackbar', {
          text: errors.file,
          color: 'red darken-3',
          timeout: 6000,
        });
      }
    },
    onFileUploaded() {
      this.fetchData();
    },
    isImage(url) {
      return (url.match(/\.(jpeg|jpg|gif|png)$/) != null);
    },
    deleteFile(fileId) {
      this.pageLoading = true;
      new OrderComplexProxy().deleteAttachment(this.$route.params.orderUuid, fileId)
        .then(response => {
          console.log(response.data);
          this.fetchData();
        })
        .catch(error => {
          console.error(error);
          return this.$router.push({
            name: 404,
          });
        })
        .finally(() => {
          this.pageLoading = false;
          this.$store.dispatch('page/finishUpdateCurrentPageContent');
        });
    },
    /* Helpers */
    updateCleaners() {
      const newVal = this.item.cleaners;
      const cleanersUuid = [];
      if (newVal && newVal.length > 0) {
        newVal.forEach(cleaner => {
          if (cleaner.uuid) {
            cleanersUuid.push(cleaner.uuid);
          }
        });
      }
      this.updateRequest('cleaners_uuid', cleanersUuid);
    },
    getCurrentLocale() {
      return window.localStorage.language;
    },
  },
};
</script>

<style scoped lang="scss">
.preview-attachment {
  background-color: #66a0f7;
  width: 100%;
  position: relative; /* If you want text inside of it */
  margin-bottom: 20px;
  max-width: 85px;
  &__content {
    display: block;
    width: 100%;
    position: absolute;
    top: 0;
    left: 0;
    text-decoration: none;

    img {
      display: block;
      width: 100%;
      object-fit: cover;
    }

    .text {
      display: flex;
      width: 100%;
      height: 100%;
      color: white;
      justify-content: center;
      align-items: center;
      font-weight: bold;
      font-size: 1em;
      text-decoration: none;
    }
  }
}
</style>
